/**
 * "สคริปต์แนะนำรายคน" (owner 17 ต.ค. 69): one script per conversation from GET …/script (api/script, ./types there),
 * the draft box's "แนะนำให้ตอบ" state, and the lean customer panel's sell actions.
 *
 * - Loaded when the draft box / customer tab of a chat is on screen (`ensure`); WS `script.updated` reloads the open
 *   chat (any other chat is reloaded the next time it is shown). A script computed before the newest customer message
 *   (`stale`) is recomputed once when the chat is opened (POST …/refresh — rules + Jev, ≈ ฿0).
 * - A customer writing again makes the suggestion answer an older message: it is hidden until the new script comes
 *   (by itself with the owner's "คิดทุกข้อความลูกค้า" on; on press of "อัปเดตคำแนะนำ" with it off).
 * - A message unsent (WS message.updated → type 'unsent'): the script may quote it — taken off the screen at once and
 *   read again (the API drops it with the unsend and recomputes from the remaining messages).
 * - The system never sends: "ใส่ในช่องพิมพ์" moves the text into the typing box (stores/inbox.ts useSuggestion).
 * - "ข้าม" hides the suggestion for this customer message; "ปรับตามแนวลูกค้า" is the existing AI draft (on press only)
 *   that re-tones the suggestion for the customer's style (`style` of the script, facts locked — api/app/ai/style_adapt.py).
 * - คู่มือปิดการขาย (owner 18 ต.ค. 69): the customer replied with something the playbook has no answer for (or no
 *   suggestion at all for the waiting messages) → the draft box shows the step card (`stepCard`: the reply + the step's
 *   question) with "ให้ AI พาไปขั้นต่อไป" (`nextStep`: POST …/draft {next_step: true}, on press only, never sent).
 * - ขั้นตอนการขาย (the panel's checklist): "ใส่ในช่องพิมพ์" puts the current step's question in the typing box (after
 *   what is there, never sent) · a step the owner added is ticked by staff (POST / DELETE …/script/steps/:key/done) ·
 *   สรุปยอด "เปิดลงออเดอร์ (เติมให้แล้ว)" (owner 18 ต.ค. 69) fills the order tab from the script's `order_prefill` the
 *   same way "สั่งชุดเดิมซ้ำ" does (pack, menus, carrier + the start day, the kitchen note, a new customer's address) —
 *   never saved: the staff pick the dishes, check and press บันทึก.
 * - "เติมจากแชททั้งหมด" (owner 18 ต.ค. 69 "สมมุติแชทนั้นคนตอบ 100% ทำไงให้ลงข้อมูลครบเหมือนใช้ AI ตอบ"): the AI reads the whole
 *   chat the staff answer themselves (POST …/order-read, ≈ ฿0.2, one call per chat + newest message) and the order tab
 *   takes it into its EMPTY fields only — pack, dishes, carrier, day, slot, kitchen note, and "เพิ่มลูกค้าใหม่ใน POS"
 *   (name, phone, place) for a chat with no POS customer (a linked one keeps the POS address; the chat's goes into a
 *   compare note). On press, and once by itself when the checklist reaches สรุปยอด for the open chat (per newest
 *   message, only while the order form has no dishes). Never in an AI-mode chat, a comment or a group; never saved.
 * - การสั่งครั้งนี้ (owner 18 ต.ค. 69 "ระวังเวลาลูกค้าเก่าสั่งซื้อใหม่ ไม่ใช่ไปอ่านออเดอร์เก่ามานะ", api session.py): every fill
 *   above reads only the purchase talked about now — the API limits the prefill and the read to the chat after the last
 *   saved order / the newest POS order / a 3-day silence and says so ("อ่านเฉพาะแชทตั้งแต่ …", first of the notes);
 *   here a prefill computed before an order the chat saved since is not used (by itself: never · on a press: the script
 *   is computed again first), the AI session's cart only when that session belongs to this purchase (lib/script.ts
 *   aiSessionUsable) — and nothing at all by itself while the chat has a held order (stores/order.ts autoFillBlocked).
 */
import { defineStore } from 'pinia'
import { ref, watch } from 'vue'
import { ApiError } from '../api/client'
import { getAiModeApi } from '../api/aimode'
import { getScriptApi } from '../api/script'
import type {
  ConversationScript,
  OrderRead,
  RepeatOrderCart,
  ScriptPlaybook,
  ScriptStep,
  ScriptSuggestion,
  ScriptUndefinedTopic,
} from '../api/script/types'
import {
  adaptStyle,
  aiCartPatch,
  aiSessionUsable,
  gapTokens,
  messageKey,
  normalizeScript,
  orderSuggestions,
  prefillOutdated,
  repeatPatch,
  suggestionText,
  withTick,
  type AdaptStyle,
} from '../lib/script'
import type { OrderForm } from '../lib/order'
import { useClock } from './clock'
import { useInbox } from './inbox'
import { useOrders } from './order'

export interface ScriptUi {
  /** The customer message the flags below belong to (lib/script.ts messageKey). */
  key: string
  /** "ข้าม" for this message. */
  skipped: boolean
  /** "ใส่ในช่องพิมพ์" done for this message (the box goes back to its quiet row). */
  used: boolean
  /** The suggestion picked with "ถัดไป" (null = the recommended one). */
  pick: string | null
  /** "ปรับตามแนวลูกค้า" (or the plain "เรียบเรียงใหม่") pressed: the AI draft box shows (until "ปิด"). */
  aiView: boolean
  /** The AI draft on screen is "ปรับตามแนวลูกค้า" (not the plain "เรียบเรียงใหม่"): "ร่างใหม่" adapts again. */
  adapt: boolean
  /** The AI draft on screen is "ให้ AI พาไปขั้นต่อไป": "ร่างใหม่" asks for it again. */
  next?: boolean
}

/** คู่มือปิดการขาย: what the draft box's step card shows (stores/script.ts `stepCard`). */
export interface StepCard {
  /** The step now (its question goes into the typing box with "ใส่ในช่องพิมพ์") — null when every step is done. */
  step: ScriptStep | null
  /** How the customer replied (null = the script read nothing special). */
  playbook: ScriptPlaybook | null
}

/** "เติมจากแชททั้งหมด" of one chat: the AI's read + which empty fields of the order tab it filled. */
export interface ChatRead extends OrderRead {
  /** Thai names of the fields it filled ("แพ็ก", "เมนู", "ลูกค้าใหม่" …) — [] = everything was picked already. */
  filled: string[]
  auto: boolean
}

const errText = (e: unknown): string => (e instanceof ApiError || e instanceof Error ? e.message : String(e))

export const useScript = defineStore('script', () => {
  // the stores this one works with, taken once (a call after an `await` could reach another app's active pinia)
  const inbox = useInbox()
  const orders = useOrders()
  const clock = useClock()
  /** conversation id → script (null = none for this chat). Absent = not loaded yet. */
  const scripts = ref<Record<string, ConversationScript | null>>({})
  const loading = ref<Record<string, boolean>>({})
  const errors = ref<Record<string, string | null>>({})
  /** A customer message came after the script was computed (hidden until the new one). */
  const outdated = ref<Record<string, boolean>>({})
  const ui = ref<Record<string, ScriptUi>>({})
  /** "สั่งชุดเดิมซ้ำ" running. */
  const repeating = ref<Record<string, boolean>>({})
  /** A step tick being saved (`<conversation id>|<step key>`). */
  const ticking = ref<Record<string, boolean>>({})
  /** "เติมจากแชททั้งหมด": running · the last read · its error (pressed only — an automatic run fails quietly). */
  const reading = ref<Record<string, boolean>>({})
  const reads = ref<Record<string, ChatRead | null>>({})
  const readErrors = ref<Record<string, string | null>>({})
  /** Automatic reads done (`<conversation id>|<newest message>`): once each. */
  const autoReads = new Set<string>()
  /** Loaded before a WS update for a chat that was not open: load again next time. */
  const dirty = new Set<string>()
  /** Stale scripts refreshed once per computation (`id|computed_at`). */
  const autoRefreshed = new Set<string>()
  const seq: Record<string, number> = {}

  let rtOff: (() => void) | null = null
  /** A customer message for a chat with a script: its suggestion now answers an older message. */
  function listen(): void {
    if (rtOff) return
    rtOff = inbox.onRealtime((e) => {
      if (!e) {
        // reconnected: events may be missed — reload the open chat's script, the rest on their next showing
        for (const id of Object.keys(scripts.value)) dirty.add(id)
        const open = inbox.selectedId
        if (open && open in scripts.value) void load(open)
        return
      }
      // a chat order changed (saved / held / paid — api/app/pos_api.py notify_order): ลูกค้าใหม่/เดิม, the steps and the
      // purchase follow it at once (owner 18 ต.ค. 69 "กดยืนยันชำระเงินแล้วแต่หน้าลูกค้าไม่อัพเดทต้องรีเฟส")
      if (e.type === 'conversation.updated' && e.pos_order) {
        const id = e.conversation.id
        if (!(id in scripts.value)) return
        if (inbox.selectedId === id) void load(id)
        else dirty.add(id)
        return
      }
      if (e.type === 'message.updated' && e.item.kind === 'message' && e.item.type === 'unsent') {
        unsent(e.conversation_id)
        return
      }
      if (e.type !== 'message.created' || e.item.kind !== 'message' || e.item.direction !== 'in') return
      if (scripts.value[e.conversation_id]) outdated.value[e.conversation_id] = true
    })
  }

  /**
   * A message of the chat was unsent (api/app/unsend.py): the script may quote it — off the screen at once, and read
   * again (the API dropped it with the unsend and recomputes it from the remaining messages).
   */
  function unsent(id: string): void {
    if (!scripts.value[id] && !loading.value[id]) return
    seq[id] = (seq[id] ?? 0) + 1 // an answer still on its way was computed before the unsend
    loading.value[id] = false
    if (scripts.value[id]) scripts.value[id] = null
    if (inbox.selectedId === id) void load(id)
    else dirty.add(id)
  }

  function uiOf(id: string): ScriptUi {
    const key = messageKey(scripts.value[id] ?? null)
    let u = ui.value[id]
    if (!u || u.key !== key) {
      // a new customer message: what was skipped / used / picked belonged to the older one
      u = { key, skipped: false, used: false, pick: null, aiView: false, adapt: false, next: false }
      ui.value[id] = u
    }
    return u
  }

  function apply(id: string, s: ConversationScript | null): void {
    const sc = s ? normalizeScript(s) : null
    scripts.value[id] = sc
    outdated.value[id] = false
    errors.value[id] = null
    uiOf(id)
    if (sc) inbox.setStyleKey(id, sc.style?.key ?? null) // the row / header badge read the same style (lib/style.ts)
    autoRead(id)
  }

  /** GET (and refresh once when it is stale). */
  async function load(id: string): Promise<void> {
    listen()
    const n = (seq[id] = (seq[id] ?? 0) + 1)
    dirty.delete(id)
    loading.value[id] = true
    try {
      const s = await getScriptApi().get(id)
      if (n !== seq[id]) return
      apply(id, s)
      if (s?.stale) {
        const k = `${id}|${s.computed_at}`
        if (!autoRefreshed.has(k)) {
          autoRefreshed.add(k)
          await refresh(id)
        }
      }
    } catch (e) {
      if (n === seq[id]) errors.value[id] = errText(e)
    } finally {
      if (n === seq[id]) loading.value[id] = false
    }
  }

  /** The chat's draft box / customer tab is on screen: load once (again after a missed update). */
  function ensure(id: string): void {
    listen()
    if (loading.value[id]) return
    if (!(id in scripts.value) || dirty.has(id)) void load(id)
  }

  /** POST …/refresh: compute now ("อัปเดตคำแนะนำ", or a stale script on opening). */
  async function refresh(id: string): Promise<void> {
    const n = (seq[id] = (seq[id] ?? 0) + 1)
    loading.value[id] = true
    try {
      const s = await getScriptApi().refresh(id)
      if (n !== seq[id]) return
      apply(id, s)
    } catch (e) {
      if (n === seq[id]) errors.value[id] = errText(e)
    } finally {
      if (n === seq[id]) loading.value[id] = false
    }
  }

  // The open chat was linked to another POS customer (or unlinked) — order tab, phone button: the script was about the
  // earlier one, read it again (the API recomputes for the new link). Not when the link is only being loaded.
  watch(
    () => {
      const id = inbox.selectedId
      return [id, id ? orders.contexts[id]?.customer_id : undefined] as const
    },
    ([id, cus], [was, before]) => {
      if (id && id === was && before !== undefined && cus !== before && id in scripts.value) void load(id)
    },
  )

  /** WS script.updated: reload the open chat now; any other chat when it is shown again. */
  function onUpdated(id: string): void {
    if (inbox.selectedId === id) void load(id)
    else if (id in scripts.value) dirty.add(id)
  }

  // ------------------------------------------------ suggestions (the draft box)
  function suggestions(id: string): ScriptSuggestion[] {
    // a chat due for ทักตาม: the follow-up card above the box sends it (platform rules, quota) — never offered twice
    // (owner 17 ต.ค. 69 "ทำงานทับซ้อนกันหรือไม่")
    const list = scripts.value[id]?.suggestions ?? []
    return orderSuggestions(inbox.convs[id]?.follow_up_due_at ? list.filter((s) => s.kind !== 'followup') : list)
  }

  /** The one on screen: the picked alternative, else the recommended one. */
  function current(id: string): ScriptSuggestion | null {
    const list = suggestions(id)
    const pick = ui.value[id]?.pick
    return (pick && list.find((s) => s.id === pick)) || list[0] || null
  }

  /** A chat the staff answer themselves (not while the AI is in charge, not an imported history chat). */
  function eligible(id: string): boolean {
    const c = inbox.convs[id] ?? inbox.details[id]
    return !!c && !c.ai_mode && !inbox.details[id]?.is_history
  }
  /** The typing box may show the script (suggestion · step card · undefined answers): eligible, and not switched off. */
  function inBox(id: string): boolean {
    return eligible(id) && !suggestOff.value
  }

  // "ปิดคำแนะนำ" (owner 18 ต.ค. 69 "มีปุ่มปิดไหม อยากลองขายมือ" → per person): this staff member's draft box shows no
  // suggestion, step card or "ยังไม่ได้กำหนดคำตอบ" in any chat (the side panel stays) until "เปิดคำแนะนำ" — kept in this
  // browser per staff id (a reload keeps it; another device starts with suggestions on)
  const suggestKey = (): string => `cf-chat.suggest-off.${inbox.me?.id ?? 'anon'}`
  const readOff = (): boolean => {
    try {
      return localStorage.getItem(suggestKey()) === '1'
    } catch {
      return false
    }
  }
  const suggestOff = ref(readOff())
  watch(
    () => inbox.me?.id,
    () => (suggestOff.value = readOff()),
  )
  /**
   * The band above the typing box (แนะนำให้ตอบ · ขั้นตอนนี้ · ให้ AI ร่างคำตอบ) opens only when the staff press "แนะนำคำตอบ"
   * (owner 18 ต.ค. 69 "จะขึ้นก็ต่อเมื่อแอดมินกด ดีกว่าขึ้นทุกข้อความ"): the chat whose band is open — one at a time, closed
   * on another chat and after a send. Tab still puts the suggestion in without opening it.
   */
  const bandOpen = ref<string | null>(null)
  function openBand(id: string): void {
    bandOpen.value = id
  }
  function toggleBand(id: string): void {
    bandOpen.value = bandOpen.value === id ? null : id
  }
  function closeBand(id?: string): void {
    if (!id || bandOpen.value === id) bandOpen.value = null
  }

  function setSuggestOff(off: boolean): void {
    suggestOff.value = off
    try {
      if (off) localStorage.setItem(suggestKey(), '1')
      else localStorage.removeItem(suggestKey())
    } catch {
      // private window / blocked storage: off for this page only
    }
  }

  /** Is the "แนะนำให้ตอบ" card in the draft box (instead of today's AI draft row)? */
  function showing(id: string): boolean {
    if (!inBox(id)) return false
    const s = scripts.value[id]
    if (!s || outdated.value[id] || s.stale) return false
    if (answered(id)) return false
    const u = ui.value[id]
    if (u && (u.skipped || u.used || u.aiView)) return false
    return !!current(id)
  }

  /** The shop already replied after the script was made for the waiting messages (here, another screen, the AI). */
  function answered(id: string): boolean {
    const s = scripts.value[id]
    const last = (inbox.convs[id] ?? inbox.details[id])?.last_message
    return !!s && s.pending.length > 0 && !!last && last.direction === 'out' && last.at > s.computed_at
  }

  /**
   * The answer types this chat's script needed but the owner has not switched an answer on for (owner 17 ต.ค. 69: the
   * system writes nothing for them) — shown while the script is the current one for the waiting messages.
   */
  function undefinedOf(id: string): ScriptUndefinedTopic[] {
    const s = scripts.value[id]
    if (!inBox(id) || !s || outdated.value[id] || s.stale || answered(id)) return []
    return s.undefined_topics ?? []
  }

  /** The quiet row may bring a skipped / used suggestion back. */
  function hidden(id: string): 'skipped' | 'used' | null {
    if (!inBox(id) || !scripts.value[id] || outdated.value[id] || !current(id)) return null
    const u = ui.value[id]
    if (!u || u.aiView) return null
    return u.skipped ? 'skipped' : u.used ? 'used' : null
  }

  function pick(id: string, suggestionId: string | null): void {
    const u = uiOf(id)
    u.pick = suggestionId
  }

  /** "ถัดไป": the next suggestion (wraps). */
  function next(id: string): void {
    const list = suggestions(id)
    if (list.length < 2) return
    const cur = current(id)
    const i = cur ? list.findIndex((s) => s.id === cur.id) : -1
    pick(id, list[(i + 1) % list.length]?.id ?? null)
  }

  function skip(id: string): void {
    uiOf(id).skipped = true
  }

  /** Bring the suggestion back (quiet row "ดูคำแนะนำ", or "ปิด" on the AI draft it opened). */
  function reshow(id: string): void {
    Object.assign(uiOf(id), { skipped: false, used: false, aiView: false, adapt: false, next: false })
  }

  /**
   * "ใส่ในช่องพิมพ์": the suggestion (or `suggestionId`) goes into the typing box — never sent. `append` keeps the
   * staff's own words above it. Returns false when there is nothing to insert.
   */
  function insert(id: string, mode: 'replace' | 'append' = 'replace', suggestionId: string | null = null): boolean {
    const s = suggestionId ? (suggestions(id).find((x) => x.id === suggestionId) ?? null) : current(id)
    if (!s) return false
    // the customer's style goes with the send when the API knows it (per-style counts, docs/script.md)
    const st = adaptStyle(scripts.value[id])
    inbox.useSuggestion(id, { text: suggestionText(s), id: s.id, gaps: gapTokens(s), style: st.known ? st.key : null }, mode)
    const u = uiOf(id)
    u.pick = s.id === suggestions(id)[0]?.id ? null : s.id
    u.used = true
    return true
  }

  /** "เรียบเรียงใหม่": today's AI draft (POST …/draft, on press only) replaces the card until "ปิด" — it rewords the
   *  suggestion on screen (an alternative picked with "ถัดไป" too), `refresh` = "ร่างใหม่" of it. The card's button is
   *  "ปรับตามแนวลูกค้า" now (`adapt`); this plain rewording stays for the API's older call. */
  function rephrase(id: string, opts: { refresh?: boolean } = {}): Promise<void> {
    Object.assign(uiOf(id), { aiView: true, adapt: false, next: false })
    return inbox.requestDraft(id, { refresh: opts.refresh, suggestionId: current(id)?.id ?? null })
  }

  /** "ปรับตามแนวลูกค้า" (owner 17 ต.ค. 69): like "เรียบเรียงใหม่", the suggestion on screen re-toned by the AI for this
   *  customer's style — numbers, dates and links kept (the API falls back to the owner's text when one got lost). */
  function adapt(id: string, opts: { refresh?: boolean } = {}): Promise<void> {
    Object.assign(uiOf(id), { aiView: true, adapt: true, next: false })
    return inbox.requestDraft(id, { refresh: opts.refresh, suggestionId: current(id)?.id ?? null, adapt: true })
  }

  /**
   * "ให้ AI พาไปขั้นต่อไป" (คู่มือปิดการขาย, owner 18 ต.ค. 69): on press only (≈ ฿0.07) the AI writes ONE message — an
   * answer to what the customer just said, then at most one question toward the chat's step now (the API reads the
   * script; facts from the system and the owner's answers only). It shows like the other AI drafts ("ใช้ร่างนี้"),
   * never sent; "ปิด" goes back to the card / the suggestion.
   */
  function nextStep(id: string, opts: { refresh?: boolean } = {}): Promise<void> {
    Object.assign(uiOf(id), { aiView: true, adapt: false, next: true })
    return inbox.requestDraft(id, { refresh: opts.refresh, nextStep: true })
  }

  /** The step now of the chat's checklist (a question step; null when every step is done or the chat has none). */
  function stepNow(id: string): ScriptStep | null {
    return scripts.value[id]?.steps.find((x) => x.status === 'current' && x.kind !== 'system') ?? null
  }

  /**
   * The draft box's step card (คู่มือปิดการขาย: "never empty"): the customer is waiting and the script has nothing to
   * suggest for it — the reply nothing in the playbook answers, or no suggestion at all — so the box shows the step's
   * question and "ให้ AI พาไปขั้นต่อไป". null while a suggestion is on screen, the AI draft is open, or nothing waits.
   */
  function stepCard(id: string): StepCard | null {
    if (!inBox(id)) return null
    const s = scripts.value[id]
    if (!s || outdated.value[id] || s.stale || answered(id) || !s.pending.length) return null
    if (current(id) || ui.value[id]?.aiView) return null
    const step = stepNow(id)
    const pb = s.playbook ?? null
    return step?.question || pb ? { step: step?.question ? step : null, playbook: pb } : null
  }

  /** The style "ปรับตามแนวลูกค้า" re-tones for (สุภาพ กระชับ when the script has none). */
  function styleOf(id: string): AdaptStyle {
    return adaptStyle(scripts.value[id])
  }

  // ------------------------------------------------ ขายต่อ: "สั่งชุดเดิมซ้ำ"
  /**
   * A cart into the order tab, then the tab is shown (the staff check and save there) — "สั่งชุดเดิมซ้ำ" and the
   * summary handoff share it. The chat's place opens "เพิ่มลูกค้าใหม่ใน POS" filled when no POS customer is linked
   * (a linked one keeps the POS address; the chat's words go into the notes).
   */
  async function fill(id: string, cart: RepeatOrderCart, kind: 'repeat' | 'chat'): Promise<ReturnType<typeof repeatPatch>> {
    // a place from the chat: whether the chat has a POS customer must be known (the order tab may not be open yet)
    await Promise.all([orders.loadCatalog().catch(() => undefined), cart.address ? orders.refreshContext(id) : null])
    const p = repeatPatch(cart, orders.catalog, clock.now)
    const linked = !!(orders.form(id).customerId || orders.contexts[id]?.customer_id)
    const notes = [...p.notes]
    if (linked && p.address?.text) notes.push(`ที่อยู่จากแชท: ${p.address.text} (ตรวจกับที่อยู่ใน POS)`)
    orders.update(id, {
      continueHeaderId: null,
      setMenuId: p.setMenuId,
      ...(p.items ? { items: p.items } : {}),
      ...(p.shipping ? { shipping: p.shipping } : {}),
      ...(p.date ? { date: p.date } : {}),
      ...(p.time ? { time: p.time } : {}),
      ...(p.remark ? { remark: p.remark } : {}),
      source: { kind, label: p.label, ...(notes.length ? { notes } : {}) },
    })
    if (p.address && !linked) orders.requestCreate(id, '', { address: p.address, shipping: p.shipping })
    inbox.panelTab = 'order'
    return p
  }

  /** The chat has a held order (or an edit is open): the order tab shows it and says what to press — the cart is not
   *  put into a new order by itself (owner 18 ต.ค. 69 "อยากให้ดูบริบทด้วยว่าลูกค้ารอโอน"). */
  async function heldFirst(id: string): Promise<boolean> {
    await orders.ensureContext(id)
    const why = orders.fillBlock(id)
    if (!why) return false
    inbox.panelTab = 'order'
    inbox.flash(why)
    return true
  }

  /** GET …/repeat-order → the order tab, filled (the staff check and save there). */
  async function repeatOrder(id: string): Promise<boolean> {
    if (repeating.value[id]) return false
    repeating.value[id] = true
    try {
      if (await heldFirst(id)) return false
      const res = await getScriptApi().repeatOrder(id)
      if (!res?.cart) {
        inbox.flash('ไม่พบชุดเดิมให้สั่งซ้ำ')
        return false
      }
      const p = await fill(id, res.cart, 'repeat')
      inbox.flash(p.packMissing ? 'แพ็กของชุดเดิมปิดขายแล้ว เลือกแพ็กใหม่ในแท็บลงออเดอร์' : 'ใส่ชุดเดิมในแท็บลงออเดอร์แล้ว ตรวจแล้วกดบันทึก')
      return true
    } catch (e) {
      inbox.flash(`สั่งชุดเดิมซ้ำไม่สำเร็จ: ${errText(e)}`)
      return false
    } finally {
      repeating.value[id] = false
    }
  }

  /**
   * The order tab opened with no pack yet (owner 18 ต.ค. 69 "ควรเลือกเซทให้ด้วย"): the cart the AI agreed with the customer
   * when an AI session of this purchase has one (aiCart — pack, dishes, carrier, day, slot), else the pack the chat chose,
   * else the one the script recommends (its note asks the staff to confirm it with the customer) — never over a pack
   * or dishes already there, never saved. The full prefill stays on สรุปยอด "เปิดลงออเดอร์ (เติมให้แล้ว)". Never while the
   * chat has a held order (its chat describes that order — owner 18 ต.ค. 69: a reload must not invite a second one).
   */
  async function suggestPack(id: string): Promise<boolean> {
    if (!(id in scripts.value)) await load(id)
    // the AI session's cart first (owner 18 ต.ค. 69 "ทำไมไม่เลือกเมนูให้ … เวลาด้วย"): when the AI talked this purchase
    // through, its pack, dishes, carrier, day and slot win over the script's text prefill (which knew the pack only)
    if (await aiCart(id)) return true
    const cart = scripts.value[id]?.order_prefill
    if (!cart?.set_menu_id && !cart?.items?.length) return false
    await orders.loadCatalog().catch(() => undefined)
    const form = orders.form(id)
    if (form.setMenuId || form.items.length || orders.autoFillBlocked(id) || beforeLastOrder(id)) return false
    const p = repeatPatch(cart, orders.catalog, clock.now)
    if (!p.setMenuId && !p.items) return false
    // the dishes too when the chat says them (a pasted web cart, the web chat's cart) — owner 18 ต.ค. 69 "ควรเลือกเมนู
    // ไว้ให้ด้วยถ้านายสรุปได้"; dishes said in free words: "อ่านเมนูนี้" on the message · from when it read (การสั่งครั้งนี้)
    const notes = p.notes.filter((n) => n === p.sessionNote || n.includes('แพ็ก') || n.includes('เมนู'))
    orders.update(id, {
      ...(p.setMenuId ? { setMenuId: p.setMenuId } : {}),
      ...(p.items ? { items: p.items } : {}),
      ...(p.time ? { time: p.time } : {}),
      source: { kind: 'chat', label: p.label, ...(notes.length ? { notes } : {}) },
    })
    return true
  }

  /**
   * A chat the AI looked after (AI ดูแลแชท): its session holds the cart the customer agreed to (api/app/ai/sales/state.py
   * `package.set_menu_id`, `items`, `shipping` method / date / time) — the order tab takes the pack, the dishes, the
   * carrier, the day (while the POS still sells it) and the slot (lib/script.ts aiCartPatch), when the session belongs to
   * this purchase (aiSessionUsable). Still only into an empty form and never saved (owner 18 ต.ค. 69 "ทำไมยังไม่เลือก
   * แพคให้" · "ทำไมไม่เลือกเมนูให้ … เวลาด้วย") — and never while the chat has a held order.
   */
  async function aiCart(id: string): Promise<boolean> {
    // a form with a cart, a held order, an earlier purchase: not even asked (the form itself is not created here)
    const before = orders.forms[id]
    if (before?.setMenuId || before?.items.length || orders.autoFillBlocked(id) || beforeLastOrder(id)) return false
    let st: Record<string, unknown> | undefined
    try {
      const sess = (await getAiModeApi().view(id)).session
      // an earlier purchase's session (its order placed, or started before the last order / a long silence): its cart
      // is not this order's (owner 18 ต.ค. 69 "ไม่ใช่ไปอ่านออเดอร์เก่ามานะ")
      if (!aiSessionUsable(sess, scripts.value[id]?.session)) return false
      st = sess?.state
    } catch {
      return false
    }
    if (!aiCartPatch(st, null)) return false // neither a pack nor dishes: nothing of the AI's to take
    await orders.loadCatalog().catch(() => undefined)
    const form = orders.form(id)
    if (form.setMenuId || form.items.length || orders.autoFillBlocked(id) || beforeLastOrder(id)) return false
    const p = aiCartPatch(st, orders.catalog, clock.now)
    if (!p || (!p.setMenuId && !p.items.length)) return false // its pack is not sold any more and no dishes: the script's
    orders.update(id, {
      ...(p.setMenuId ? { setMenuId: p.setMenuId } : {}),
      ...(p.items.length ? { items: p.items } : {}),
      ...(p.shipping ? { shipping: p.shipping } : {}),
      ...(p.date ? { date: p.date } : {}),
      ...(p.time ? { time: p.time } : {}),
      source: {
        kind: 'chat',
        label: ['เติมจากที่ AI คุยกับลูกค้า', p.setMenuId ? p.packName : null].filter(Boolean).join(' · '),
        ...(p.notes.length ? { notes: p.notes } : {}),
      },
    })
    return true
  }

  /** การสั่งครั้งนี้: the script was computed before an order the chat saved since — its prefill (and the boundary the AI
   *  session is checked against) belong to that order: nothing is filled by itself (the next purchase's messages bring a
   *  new script). */
  function beforeLastOrder(id: string): boolean {
    return prefillOutdated(scripts.value[id], orders.contexts[id]?.chat_orders)
  }

  /**
   * การสั่งครั้งนี้, on a press: the script was computed before an order the chat saved since (done / held) — its prefill
   * is about that order. Computed again first (the API starts the new purchase after it); false while it still is.
   */
  async function purchaseIsCurrent(id: string): Promise<boolean> {
    const chatOrders = () => orders.contexts[id]?.chat_orders
    if (!prefillOutdated(scripts.value[id], chatOrders())) return true
    await refresh(id)
    return !prefillOutdated(scripts.value[id], chatOrders())
  }

  /** สรุปยอด "เปิดลงออเดอร์ (เติมให้แล้ว)": the script's `order_prefill` → the order tab (never saved). */
  async function fillFromChat(id: string): Promise<boolean> {
    if (repeating.value[id]) return false
    repeating.value[id] = true
    try {
      if (await heldFirst(id)) return false // the held order first: says what to press
      const cart = (await purchaseIsCurrent(id)) ? scripts.value[id]?.order_prefill : null
      if (!cart) {
        inbox.flash('ยังไม่มีข้อมูลจากแชทให้เติม')
        return false
      }
      const p = await fill(id, cart, 'chat')
      inbox.flash(
        p.packMissing
          ? 'แพ็กที่ลูกค้าเลือกปิดขายแล้ว เลือกแพ็กในแท็บลงออเดอร์'
          : 'เติมแท็บลงออเดอร์จากแชทแล้ว เลือกเมนู ตรวจ แล้วกดบันทึก',
      )
      return true
    } catch (e) {
      inbox.flash(`เติมแท็บลงออเดอร์ไม่สำเร็จ: ${errText(e)}`)
      return false
    } finally {
      repeating.value[id] = false
    }
  }

  // ------------------------------------------------ "เติมจากแชททั้งหมด"
  /** A chat the AI may read for the order tab: staff answer it (not AI mode, not imported history), a direct chat. */
  function readable(id: string): boolean {
    const c = inbox.convs[id] ?? inbox.details[id]
    return eligible(id) && c?.kind === 'dm'
  }

  /** The order form has no dishes, no open pack being continued, no saved / waiting order, no held bill waiting for
   *  the transfer ("ยังไม่โอน · ส่ง QR ให้ลูกค้า": the order of this sale is made already). */
  function emptyish(id: string): boolean {
    const f = orders.forms[id]
    if (orders.results[id] || orders.openOrders[id] || orders.heldOrders(id).length) return false
    return !f || (!f.items.length && !f.continueHeaderId)
  }

  /** Every question step of the checklist is done (สรุปยอด reached) and the chat has no order yet. */
  function atSummary(s: ConversationScript): boolean {
    const questions = s.steps.filter((x) => x.kind !== 'system')
    const pay = s.steps.find((x) => x.key === 'pay')
    return questions.length > 0 && questions.every((x) => x.status === 'done') && pay?.status !== 'done'
  }

  /** Once per newest message: the open chat reached สรุปยอด and its order form is still empty → read by itself. */
  function autoRead(id: string): void {
    const s = scripts.value[id]
    if (!s || s.stale || outdated.value[id] || inbox.selectedId !== id || !readable(id) || !atSummary(s)) return
    const last = (inbox.convs[id] ?? inbox.details[id])?.last_message?.at ?? s.computed_at
    const key = `${id}|${last}`
    if (autoReads.has(key) || reading.value[id] || !emptyish(id)) return
    autoReads.add(key)
    void readChat(id, { auto: true })
  }

  /**
   * The AI's read into the order tab — EMPTY fields only (what the staff picked stays): the pack, the dishes, the carrier
   * and the slot while the day / slot are untouched, the day while it is still the default, the kitchen note; a chat
   * with no POS customer opens "เพิ่มลูกค้าใหม่ใน POS" filled (its own empty fields only) — a linked one keeps the POS
   * address and the chat's goes into a compare note (as "เปิดลงออเดอร์ (เติมให้แล้ว)"). Returns what it filled.
   */
  async function applyRead(id: string, res: OrderRead): Promise<string[]> {
    const place = res.customer.address ?? res.cart.address ?? null
    await Promise.all([orders.loadCatalog().catch(() => undefined), place ? orders.refreshContext(id).catch(() => undefined) : null])
    const f = orders.form(id)
    const p = repeatPatch({ ...res.cart, address: place }, orders.catalog, clock.now)
    const linked = !!(f.customerId || orders.contexts[id]?.customer_id)
    const patch: Partial<OrderForm> = {}
    const filled: string[] = []
    if (!f.setMenuId && !f.continueHeaderId && p.setMenuId) {
      patch.setMenuId = p.setMenuId
      filled.push('แพ็ก')
    }
    if (!f.items.length && !f.continueHeaderId && p.items) {
      patch.items = p.items
      filled.push('เมนู')
    }
    const untouched = f.dateAuto && f.time === null // the staff picked no day and no slot yet
    if (untouched && p.shipping && p.shipping !== f.shipping) {
      patch.shipping = p.shipping
      filled.push('ขนส่ง')
    }
    if (f.dateAuto && p.date) {
      patch.date = p.date
      filled.push('วันส่ง')
    }
    if (f.time === null && p.time && (patch.shipping ?? f.shipping) !== 'dhl') {
      patch.time = p.time
      filled.push('รอบเวลา')
    }
    if (!f.remark.trim() && p.remark) {
      patch.remark = p.remark
      filled.push('หมายเหตุถึงครัว')
    }
    const notes = p.notes.filter((n) => n !== p.sessionNote) // the read bar shows from when it read (ChatReadBar)
    if (linked && place?.text) notes.push(`ที่อยู่จากแชท: ${place.text} (ตรวจกับที่อยู่ใน POS)`)
    if (!f.source) patch.source = { kind: 'chat', label: p.label, ...(notes.length ? { notes } : {}) }
    else if (notes.some((n) => !f.source?.notes?.includes(n))) {
      patch.source = { ...f.source, notes: [...new Set([...(f.source.notes ?? []), ...notes])] }
    }
    if (Object.keys(patch).length) orders.update(id, patch)
    const c = res.customer
    if (!linked && (c.name || c.phone || place)) {
      orders.requestCreate(id, c.phone ?? '', { address: place, shipping: p.shipping, name: c.name, keep: true })
      filled.push('ลูกค้าใหม่')
    }
    return filled
  }

  /** "เติมจากแชททั้งหมด" pressed (or the automatic run at สรุปยอด): POST …/order-read → the order tab's empty fields. */
  async function readChat(id: string, opts: { auto?: boolean } = {}): Promise<boolean> {
    // the held order first / an edit open: nothing is read into a form (the button is not shown then either)
    if (reading.value[id] || !readable(id) || orders.fillBlock(id)) return false
    reading.value[id] = true
    if (!opts.auto) readErrors.value[id] = null
    try {
      const res = await getScriptApi().orderRead(id, { auto: !!opts.auto })
      const filled = await applyRead(id, res)
      reads.value[id] = { ...res, filled, auto: !!opts.auto }
      readErrors.value[id] = null
      if (opts.auto && filled.length) inbox.flash('AI อ่านแชทแล้ว เติมแท็บลงออเดอร์ให้ ตรวจก่อนบันทึก')
      return true
    } catch (e) {
      if (!opts.auto) readErrors.value[id] = errText(e) // an automatic run nobody asked for stays quiet
      return false
    } finally {
      reading.value[id] = false
    }
  }

  // ------------------------------------------------ ขายต่อ: ขั้นตอนการขาย
  /**
   * "ใส่ในช่องพิมพ์" of a step: its question goes into the typing box — after the words already there (the questions go
   * last), alone in an empty box. Never sent. The send counts it as `step:<key>` (GET /script/usage).
   */
  function insertStep(id: string, key: string): boolean {
    const q = scripts.value[id]?.steps.find((s) => s.key === key)?.question?.trim()
    if (!q) return false
    const text = inbox.composers[id]?.text ?? ''
    if (text.includes(q)) {
      inbox.flash('คำถามนี้อยู่ในช่องพิมพ์แล้ว')
      return false
    }
    inbox.useSuggestion(id, { text: q, id: `step:${key}`, gaps: [] }, text.trim() ? 'append' : 'replace')
    inbox.flash('ใส่คำถามในช่องพิมพ์แล้ว ตรวจแล้วกดส่ง')
    return true
  }

  /** Tick / untick a step the owner added: shown at once, then the API's script (back as it was when it fails). */
  async function tickStep(id: string, key: string, done: boolean): Promise<boolean> {
    const k = `${id}|${key}`
    const s = scripts.value[id]
    if (ticking.value[k] || !s) return false
    ticking.value[k] = true
    const before = s.steps
    s.steps = withTick(before, key, done)
    try {
      apply(id, await getScriptApi().stepDone(id, key, done))
      return true
    } catch (e) {
      const now = scripts.value[id]
      if (now) now.steps = before
      inbox.flash(`บันทึกขั้น "${before.find((x) => x.key === key)?.label ?? key}" ไม่สำเร็จ: ${errText(e)}`)
      return false
    } finally {
      ticking.value[k] = false
    }
  }

  return {
    suggestOff,
    setSuggestOff,
    bandOpen,
    openBand,
    toggleBand,
    closeBand,
    scripts,
    loading,
    errors,
    outdated,
    ui,
    repeating,
    ticking,
    reading,
    reads,
    readErrors,
    readable,
    readChat,
    ensure,
    eligible,
    load,
    suggestPack,
    refresh,
    onUpdated,
    suggestions,
    current,
    showing,
    undefinedOf,
    hidden,
    pick,
    next,
    skip,
    reshow,
    insert,
    rephrase,
    adapt,
    nextStep,
    stepNow,
    stepCard,
    styleOf,
    repeatOrder,
    fillFromChat,
    insertStep,
    tickStep,
  }
})
